'use client';

import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Text, ModalNext } from '@instamenu/ui-system-next';
import { useCheckoutContext, findInvalidModifierGroups } from '@instamenu/context-checkout';
import { useRouter } from 'next/navigation';
import { makePath } from '../utils/path-utils';
import toast from 'react-hot-toast';
import { Gift, UtensilsCrossed, ChevronLeft } from 'lucide-react';
import { themeVars } from '@instamenu/ui-vanilla-extract';
import { MenuItemProvider, useMenuItemContext } from '../menu-item/menu-item-context';
import { ModifierGroupView } from '../menu-item/menu-item-details/modifier-group-view';
import { SpecialInstructions } from '../menu-item/menu-item-details/special-instructions';
import {
  modalHeader,
  balanceBadge,
  modalBody,
  sectionLabel,
  itemGrid,
  itemCard,
  itemCardDisabled,
  itemImage,
  itemImagePlaceholder,
  itemInfo,
  itemName,
  itemPoints,
  itemPointsAffordable,
  emptyState,
  detailImage,
  warningBox,
  stickyFooter,
  backButton,
  redeemModalContainer,
} from './reward-modal.css';

type RewardModalProps = {
  open: boolean;
  onClose: () => void;
  onRewardApplied: () => void;
  rewardItemGroupIds: string[];
  pointsBalance?: number;
  isItemBased?: boolean;
  earningThreshold?: number;
  rewardsAvailable?: number;
  progress?: number;
  rewardIncludesModifiers?: boolean;
};

/** Inline item detail view for redeeming with points */
const RedeemItemDetail = observer(
  ({
    pointsBalance,
    onRedeem,
    isApplying,
    isItemBased,
    rewardIncludesModifiers,
  }: {
    pointsBalance: number;
    onRedeem: () => void;
    isApplying: boolean;
    isItemBased: boolean;
    rewardIncludesModifiers: boolean;
  }) => {
    const ctx = useMenuItemContext();
    if (!ctx) return null;

    // `price` from MenuItemContext is already `(item.price + modifiers) * quantity`
    // (see menu-item-context.tsx) — do NOT multiply by quantity again at the call sites.
    const { item, price, modifiers, setModifierGroupErrors } = ctx;
    const canAfford = isItemBased ? true : pointsBalance >= price;

    const handleRedeem = () => {
      const modifierErrors = findInvalidModifierGroups(item, modifiers);
      if (modifierErrors?.length) {
        setModifierGroupErrors(modifierErrors);
        return;
      }
      setModifierGroupErrors([]);
      onRedeem();
    };

    return (
      <>
        {item.imageUrl && <img src={item.imageUrl} alt={item.name ?? ''} className={detailImage} />}

        <Text variant="h3" css={{ marginBottom: 4 }}>
          {item.name}
        </Text>
        {item.description && (
          <Text variant="caption1" css={{ color: '#6B6868', marginBottom: 12 }}>
            {item.description}
          </Text>
        )}

        {!rewardIncludesModifiers && (
          <div className={warningBox}>
            {isItemBased
              ? 'Add-ons will be charged separately.'
              : 'Add-ons cost additional points.'}
          </div>
        )}
        <div style={{ marginBottom: 16 }}>
          {item.modifierGroups?.map((modifierGroup) => (
            <ModifierGroupView key={modifierGroup.id} data={modifierGroup} />
          ))}
          <SpecialInstructions />
        </div>

        <div className={stickyFooter}>
          <Button
            onClick={handleRedeem}
            disabled={!canAfford || isApplying}
            loading={isApplying}
            style={{ width: '100%' }}
          >
            {isItemBased
              ? 'Redeem free item'
              : canAfford
                ? `Redeem — ${price.toLocaleString()} pts`
                : `Need ${(price - pointsBalance).toLocaleString()} more pts`}
          </Button>
        </div>
      </>
    );
  },
);

/** Bridge that reads MenuItemContext and calls add+redeem */
const RedeemItemDetailBridge = observer(
  ({
    pointsBalance,
    onAddAndRedeem,
    isApplying,
    isItemBased,
    rewardIncludesModifiers,
  }: {
    pointsBalance: number;
    isApplying: boolean;
    isItemBased: boolean;
    rewardIncludesModifiers: boolean;
    onAddAndRedeem: (ctx: {
      item: { id: string };
      quantity: number;
      modifiers: { id: string; price: number; [key: string]: unknown }[];
      instructions?: string;
    }) => void;
  }) => {
    const ctx = useMenuItemContext();
    if (!ctx) return null;

    return (
      <RedeemItemDetail
        pointsBalance={pointsBalance}
        isApplying={isApplying}
        isItemBased={isItemBased}
        rewardIncludesModifiers={rewardIncludesModifiers}
        onRedeem={() => {
          onAddAndRedeem({
            item: ctx.item,
            quantity: ctx.quantity,
            modifiers: ctx.modifiers.map((m) => ({ ...m, price: m.price ?? 0 })),
            instructions: ctx.instructions,
          });
        }}
      />
    );
  },
);

export const RewardModal = observer(
  ({
    open,
    onClose,
    onRewardApplied,
    rewardItemGroupIds,
    pointsBalance = 0,
    isItemBased = false,
    earningThreshold = 10,
    rewardsAvailable = 0,
    progress = 0,
    rewardIncludesModifiers = true,
  }: RewardModalProps) => {
    const checkout = useCheckoutContext();
    const order = checkout?.order;
    const restaurantId = checkout?.restaurant.id ?? order?.restaurant.id;
    const router = useRouter();
    const [applying, setApplying] = useState<string | null>(null);
    const applyingRef = useRef(false);
    const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
    const brandColor = themeVars.color.brand;

    // Reset selection when modal closes
    useEffect(() => {
      if (!open) setSelectedItemId(null);
    }, [open]);

    // Cart items eligible for redemption
    const cartItems = useMemo(() => {
      const nonComped = order?.orderItems.filter((oi) => !oi.loyaltyComped) ?? [];
      return nonComped.filter((oi) => {
        if (rewardItemGroupIds.length === 0) return true;
        return rewardItemGroupIds.includes(oi.item.itemGroup?.id ?? '');
      });
    }, [order?.orderItems, rewardItemGroupIds]);

    // Menu items the customer can afford (not in cart)
    const menuItems = useMemo(() => {
      if (!checkout) return [];
      const cartItemIds = new Set(
        order?.orderItems.filter((oi) => !oi.loyaltyComped).map((oi) => oi.item.id) ?? [],
      );
      const allItems = checkout.menu.itemGroups.flatMap((ig) => {
        if (rewardItemGroupIds.length > 0 && !rewardItemGroupIds.includes(ig.id)) return [];
        return ig.items
          .filter((item) => !item.outOfStock)
          .map((item) => ({
            id: item.id,
            name: item.name,
            price: item.price,
            imageUrl: item.imageUrl,
            groupName: ig.name,
          }));
      });
      const notInCart = allItems.filter((item) => !cartItemIds.has(item.id));
      const affordable = isItemBased
        ? rewardsAvailable > 0
          ? notInCart
          : []
        : notInCart.filter((item) => item.price <= pointsBalance);
      return affordable.sort((a, b) => a.price - b.price);
    }, [
      checkout,
      order?.orderItems,
      rewardItemGroupIds,
      pointsBalance,
      isItemBased,
      rewardsAvailable,
    ]);

    const handleApply = useCallback(
      async (orderItemId: string) => {
        if (applyingRef.current || !checkout) return;
        applyingRef.current = true;
        setApplying(orderItemId);

        try {
          checkout.loyalty.markApplying();
          await checkout.order.applyReward(orderItemId);
          onRewardApplied();
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Something went wrong';
          toast.error(`Couldn't apply reward: ${message}`);
        } finally {
          checkout.loyalty.markApplied();
          applyingRef.current = false;
          setApplying(null);
        }
      },
      [checkout, onRewardApplied],
    );

    /** Add item to cart + immediately apply reward */
    const handleAddAndRedeem = useCallback(
      async (ctx: {
        item: { id: string };
        quantity: number;
        modifiers: { id: string; price: number; [key: string]: unknown }[];
        instructions?: string;
      }) => {
        if (applyingRef.current || !checkout || !order) return;
        applyingRef.current = true;
        setApplying('adding');

        try {
          const menuItem = checkout.menu.itemById(ctx.item.id);
          if (!menuItem) throw new Error('Item not found');

          const added = await checkout.order.addOrderItem({
            item: menuItem,
            quantity: ctx.quantity,
            modifiers: ctx.modifiers,
            instructions: ctx.instructions,
            skipMerge: true,
          });
          if (!added?.id) throw new Error('Item not found in cart after adding');

          checkout.loyalty.markApplying();
          await checkout.order.applyReward(added.id);
          setSelectedItemId(null);
          onRewardApplied();
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Something went wrong';
          toast.error(`Couldn't redeem: ${message}`);
        } finally {
          checkout.loyalty.markApplied();
          applyingRef.current = false;
          setApplying(null);
        }
      },
      [checkout, order, onRewardApplied],
    );

    if (!open) return null;

    const hasCartItems = cartItems.length > 0;
    const hasMenuItems = menuItems.length > 0;

    // Item detail view (modifier selection + redeem)
    if (selectedItemId && checkout) {
      const menuData = checkout.order.restaurant.json?.currentMenu;
      if (menuData) {
        return (
          <ModalNext
            opened
            onRequestClose={onClose}
            withCloseButton
            containerStyle={{ padding: 0 }}
          >
            <div className={redeemModalContainer}>
              <div className={modalHeader} style={{ flexShrink: 0 }}>
                <div onClick={() => setSelectedItemId(null)} className={backButton}>
                  <ChevronLeft size={16} /> Back
                </div>
                <div
                  className={balanceBadge}
                  style={{ backgroundColor: `${brandColor}18`, color: brandColor }}
                >
                  <Gift size={14} />
                  {isItemBased
                    ? `${rewardsAvailable} free ${rewardsAvailable === 1 ? 'item' : 'items'}`
                    : `${pointsBalance.toLocaleString()} pts`}
                </div>
              </div>
              <div className={modalBody} style={{ overflow: 'auto', flex: 1 }}>
                <MenuItemProvider
                  menu={menuData}
                  itemId={selectedItemId}
                  isPointRedemption={!isItemBased}
                  rewardIncludesModifiers={rewardIncludesModifiers}
                >
                  <RedeemItemDetailBridge
                    pointsBalance={pointsBalance}
                    onAddAndRedeem={handleAddAndRedeem}
                    isApplying={applying === 'adding'}
                    isItemBased={isItemBased}
                    rewardIncludesModifiers={rewardIncludesModifiers}
                  />
                </MenuItemProvider>
              </div>
            </div>
          </ModalNext>
        );
      }
    }

    // Grid view
    return (
      <ModalNext opened onRequestClose={onClose} withCloseButton containerStyle={{ padding: 0 }}>
        <div className={redeemModalContainer}>
          <div className={modalHeader} style={{ flexShrink: 0 }}>
            <div>
              <Text variant="h2">{isItemBased ? 'Redeem free item' : 'Redeem points'}</Text>
            </div>
            <div
              className={balanceBadge}
              style={{ backgroundColor: `${brandColor}18`, color: brandColor }}
            >
              <Gift size={14} />
              {isItemBased
                ? `${rewardsAvailable} free ${rewardsAvailable === 1 ? 'item' : 'items'}`
                : `${pointsBalance.toLocaleString()} pts`}
            </div>
          </div>

          <div className={modalBody} style={{ overflow: 'auto', flex: 1 }}>
            {!hasCartItems && !hasMenuItems ? (
              <div className={emptyState}>
                <UtensilsCrossed size={32} color="#ccc" />
                <Text variant="body1" css={{ color: '#6B6868' }}>
                  No items available for your points yet. Keep earning!
                </Text>
                <Button
                  size="xs"
                  disabled={!restaurantId}
                  onClick={() => {
                    if (!restaurantId) return;
                    onClose();
                    router.push(makePath({ restaurantId, menuId: checkout?.menuId }));
                  }}
                >
                  Browse menu
                </Button>
              </div>
            ) : (
              <>
                {hasCartItems && (
                  <>
                    <div className={sectionLabel}>In your cart</div>
                    <div className={itemGrid}>
                      {cartItems.map((oi) => {
                        const cost = isItemBased ? earningThreshold : oi.unitPrice;
                        const canAfford = isItemBased
                          ? rewardsAvailable > 0
                          : pointsBalance >= cost;
                        return (
                          <div
                            key={oi.id}
                            className={`${itemCard} ${!canAfford ? itemCardDisabled : ''}`}
                            onClick={() => !applying && canAfford && oi.id && handleApply(oi.id)}
                          >
                            {oi.item.imageUrl ? (
                              <img
                                src={oi.item.imageUrl}
                                alt={oi.item.name ?? ''}
                                className={itemImage}
                              />
                            ) : (
                              <div className={itemImagePlaceholder}>
                                <UtensilsCrossed size={20} />
                              </div>
                            )}
                            <div className={itemInfo}>
                              <div className={itemName}>{oi.item.name}</div>
                              <div
                                className={`${itemPoints} ${canAfford ? itemPointsAffordable : ''}`}
                                style={canAfford ? { color: brandColor } : undefined}
                              >
                                {isItemBased
                                  ? 'FREE'
                                  : canAfford
                                    ? `FREE · ${cost.toLocaleString()} pts`
                                    : `${cost.toLocaleString()} pts`}
                              </div>
                              {canAfford && (
                                <div
                                  style={{
                                    marginTop: 4,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: brandColor,
                                  }}
                                >
                                  {applying === oi.id ? '...' : 'Tap to redeem'}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {hasMenuItems && (
                  <>
                    <div className={sectionLabel}>
                      {hasCartItems
                        ? 'Add from menu'
                        : isItemBased
                          ? 'Pick your free item'
                          : 'Available items'}
                    </div>
                    <div className={itemGrid}>
                      {menuItems.map((item) => (
                        <div
                          key={item.id}
                          className={itemCard}
                          onClick={() => setSelectedItemId(item.id)}
                        >
                          {item.imageUrl ? (
                            <img src={item.imageUrl} alt={item.name ?? ''} className={itemImage} />
                          ) : (
                            <div className={itemImagePlaceholder}>
                              <UtensilsCrossed size={20} />
                            </div>
                          )}
                          <div className={itemInfo}>
                            <div className={itemName}>{item.name}</div>
                            <div
                              className={`${itemPoints} ${itemPointsAffordable}`}
                              style={{ color: brandColor }}
                            >
                              {isItemBased ? 'FREE' : `${item.price.toLocaleString()} pts`}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </ModalNext>
    );
  },
);
